import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { Modal, Button } from 'react-bootstrap';
import { AuthContext } from '../../../component/context/AuthContext';
import * as CONST from '../../../component/config/Constant';
import { toast } from 'react-toastify';
import { IconClock, IconList, IconCircleCheck } from '@tabler/icons-react';

export default function ViewSetModal({ show, handleClose, setId, questionId }) {
    const [authUserData] = useContext(AuthContext);
    const token = authUserData && authUserData[0] ? authUserData[0].token : '';

    const [loading, setLoading] = useState(false);
    const [setData, setSetData] = useState(null);
    const [questionData, setQuestionData] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const questionsPerPage = 5;

    useEffect(() => {
        if (show) {
            if (setId) {
                setCurrentPage(1);
                fetchSetDetails();
                setQuestionData(null);
            } else if (questionId) {
                fetchQuestionDetails();
                setSetData(null);
            }
        }
    }, [show, setId, questionId]);

    async function fetchSetDetails() {
        setLoading(true);
        try {
            const resp = await axios.get(CONST.ISO_SET_GET + setId, {
                headers: { Authorization: `Basic ${token}` },
            });
            if (resp.data?.status === 'success') {
                setSetData(resp.data.data);
            } else {
                toast.error("Failed to load details");
            }
        } catch (err) {
            toast.error("Error fetching details");
        } finally {
            setLoading(false);
        }
    }

    async function fetchQuestionDetails() {
        setLoading(true);
        try {
            const resp = await axios.get(CONST.ISO_GET_QUESTION + questionId, {
                headers: { Authorization: `Basic ${token}` },
            });
            if (resp.data?.status === 'success') {
                setQuestionData(resp.data.data);
            } else {
                toast.error("Failed to load question details");
            }
        } catch (err) {
            toast.error("Error fetching question details");
        } finally {
            setLoading(false);
        }
    }

    const allQuestions = setId ? (setData?.questions || []) : (questionData ? [questionData] : []);
    const totalPages = Math.ceil(allQuestions.length / questionsPerPage);
    const paginatedQuestions = allQuestions.slice(
        (currentPage - 1) * questionsPerPage,
        currentPage * questionsPerPage
    );

    return (
        <Modal size="lg" show={show} onHide={handleClose} centered scrollable>
            <Modal.Header closeButton className="bg-light">
                <Modal.Title className="fw-bold">
                    {setData ? `Set: ${setData.title}` : (questionData ? "Question Details" : "Details")}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4 bg-light-subtle">
                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-3 text-muted">Retrieving details...</p>
                    </div>
                ) : (setData || questionData) ? (
                    <div>
                        {setData && (
                            <div className="row g-3 mb-4">
                                <div className="col-12">
                                    <div className="card shadow-none border-0 bg-white p-3 rounded-3">
                                        <h6 className="text-muted text-uppercase small fw-bold mb-2">Description</h6>
                                        <p className="mb-0 text-dark">{setData.description || "No description provided."}</p>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card shadow-none border bg-white p-3 rounded-3 h-100 d-flex flex-row align-items-center gap-3">
                                        <div className="avatar bg-blue-lt rounded">
                                            <IconClock size={20} />
                                        </div>
                                        <div>
                                            <div className="small text-muted mb-1">Time Limit</div>
                                            <div className="fw-bold">{setData.time_limit_minutes} Mins</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card shadow-none border bg-white p-3 rounded-3 h-100 d-flex flex-row align-items-center gap-3">
                                        <div className="avatar bg-green-lt rounded">
                                            <IconList size={20} />
                                        </div>
                                        <div>
                                            <div className="small text-muted mb-1">Questions</div>
                                            <div className="fw-bold">{setData.questions?.length || 0} Items</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card shadow-none border bg-white p-3 rounded-3 h-100 d-flex flex-row align-items-center gap-3">
                                        <div className="avatar bg-purple-lt rounded">
                                            <IconCircleCheck size={20} />
                                        </div>
                                        <div>
                                            <div className="small text-muted mb-1">Status</div>
                                            <span className={`badge ${setData.is_active ? 'bg-success-lt' : 'bg-secondary-lt'}`}>
                                                {setData.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="d-flex align-items-center justify-content-between mb-3 mt-4">
                            <h5 className="mb-0 fw-bold">Questions & Options</h5>
                            <span className="badge bg-primary-lt">Total {allQuestions.length}</span>
                        </div>

                        {paginatedQuestions.map((q, qIndex) => {
                            const actualIndex = (currentPage - 1) * questionsPerPage + qIndex + 1;
                            return (
                                <div
                                    key={q.id}
                                    className={`card mb-4 rounded-3 transition-all border ${hoveredId === q.id ? 'border-primary shadow-sm' : 'border-light'}`}
                                    style={{ transition: 'all 0.3s ease', borderWidth: '2px' }}
                                    onMouseEnter={() => setHoveredId(q.id)}
                                    onMouseLeave={() => setHoveredId(null)}
                                >
                                    <div className="card-body p-4">
                                        <div className="d-flex align-items-center justify-content-start mb-4">
                                            <div
                                                className="align-items-center justify-content-center text-white rounded-circle d-flex me-2 mr-3"
                                                style={{ width: "32px", height: "32px", fontSize: "14px", backgroundColor: "#0d2543" }}
                                            >
                                                {actualIndex}
                                            </div>

                                            <h5 className="mb-0 fw-bold text-dark">
                                                {q.question_text}
                                            </h5>
                                        </div>

                                        <div className="ms-5 row gx-4 gy-2">
                                            {(q.question_options || q.options || []).map((opt) => {
                                                // Only show the green highlight if we are viewing the specific Question Details (questionId)
                                                const shouldHighlight = !!questionId;
                                                const isCorrect = (String(opt.is_correct) === "1" || opt.is_correct === true) && shouldHighlight;
                                                
                                                return (
                                                    <div key={opt.id} className="col-md-6 mb-2">
                                                        <div className={`p-2 px-3 border rounded-pill d-flex align-items-center gap-3 h-100 transition-all
                                                        ${isCorrect
                                                                ? 'bg-success-lt border-success text-success fw-bold'
                                                                : 'bg-white border-secondary-subtle text-muted shadow-none'}`}>
                                                            {isCorrect ? <IconCircleCheck size={18} /> : <div className="rounded-circle border" style={{ width: 14, height: 14, minWidth: 14, background: '#f8f9fa' }}></div>}
                                                            <span className="text-truncate ml-2">{opt.option_text}</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div className="text-center text-muted p-5 bg-white rounded-3">
                        <p className="mb-0">No data available</p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-light p-2">
                {!loading && totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center w-100 px-3">
                        <span className="text-muted small">Page {currentPage} of {totalPages}</span>
                        <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>«</button>
                            </li>
                            {[...Array(totalPages)].map((_, i) => (
                                <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>»</button>
                            </li>
                        </ul>
                    </div>
                )}
                <div className="w-100 text-end p-2 border-top">
                    <Button variant="outline-secondary" className="px-4 rounded-pill" onClick={handleClose}>
                        Close
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
